import { ReactNode } from 'react';

import {
  BlockersCell,
  BoolCell,
  ErrorCell,
  StatusBadge,
  formatDateTime,
  formatReleaseDate,
} from './cells';

import { VisionMonitorRow } from '@/services/VisionMonitorService';

export type ColumnGroup =
  | 'Product'
  | 'Vision category'
  | 'Vision'
  | 'Catalog'
  | 'SEO'
  | 'Customer';

export interface MonitorColumn {
  key: string;
  label: string;
  group: ColumnGroup;
  className?: string;
  render: (row: VisionMonitorRow) => ReactNode;
}

const dash = <span className="text-gray-400">—</span>;
const text = (value: string | number | null | undefined) =>
  value === null || value === undefined || value === '' ? dash : value;

// Column order follows the dashboard spec; group headers sit above them.
export const MONITOR_COLUMNS: MonitorColumn[] = [
  {
    key: 'catalogId',
    label: 'Catalog ID',
    group: 'Product',
    className: 'font-mono',
    render: (row) => row.catalogId,
  },
  {
    key: 'productId',
    label: 'Product ID',
    group: 'Product',
    className: 'font-mono',
    render: (row) => row.productId,
  },
  {
    key: 'sku',
    label: 'SKU',
    group: 'Product',
    className: 'font-mono',
    render: (row) => text(row.sku),
  },
  {
    key: 'productName',
    label: 'Product name',
    group: 'Product',
    className: 'min-w-[220px]',
    render: (row) => text(row.productName),
  },
  {
    key: 'creationDate',
    label: 'Creation date',
    group: 'Product',
    className: 'whitespace-nowrap',
    render: (row) => formatDateTime(row.creationDate),
  },
  {
    key: 'releaseDate',
    label: 'Release date',
    group: 'Product',
    className: 'whitespace-nowrap',
    render: (row) => formatReleaseDate(row.releaseDate),
  },
  {
    key: 'visionCategory',
    label: 'Vision category',
    group: 'Vision category',
    className: 'min-w-[160px]',
    render: (row) => text(row.visionCategory),
  },
  {
    key: 'visionCategoryStatus',
    label: 'Status',
    group: 'Vision category',
    render: (row) => <StatusBadge status={row.visionCategoryStatus} />,
  },
  {
    key: 'visionCategoryError',
    label: 'Error',
    group: 'Vision category',
    render: (row) => (
      <ErrorCell
        title="Vision category error"
        message={row.visionCategoryError}
      />
    ),
  },
  {
    key: 'visionCategoryAttempts',
    label: 'Attempts',
    group: 'Vision category',
    className: 'text-center',
    render: (row) => row.visionCategoryAttempts,
  },
  {
    key: 'categoryBlockers',
    label: 'Blocked by',
    group: 'Vision category',
    render: (row) => <BlockersCell blockers={row.categoryBlockers} />,
  },
  {
    key: 'needVisionRun',
    label: 'Need run',
    group: 'Vision',
    render: (row) => <BoolCell value={row.needVisionRun} />,
  },
  {
    key: 'sentToVision',
    label: 'Sent',
    group: 'Vision',
    render: (row) => <BoolCell value={row.sentToVision} />,
  },
  {
    key: 'visionRun',
    label: 'Run',
    group: 'Vision',
    render: (row) => <BoolCell value={row.visionRun} />,
  },
  {
    key: 'visionStatus',
    label: 'Status',
    group: 'Vision',
    render: (row) => <StatusBadge status={row.visionStatus} />,
  },
  {
    key: 'visionError',
    label: 'Error',
    group: 'Vision',
    render: (row) => (
      <ErrorCell title="Vision error" message={row.visionError} />
    ),
  },
  {
    key: 'visionAttempts',
    label: 'Attempts',
    group: 'Vision',
    className: 'text-center',
    render: (row) => row.visionAttempts,
  },
  {
    key: 'visionReadCount',
    label: 'Reads',
    group: 'Vision',
    className: 'text-center',
    render: (row) => row.visionReadCount,
  },
  {
    key: 'visionFirstReadAt',
    label: 'First read',
    group: 'Vision',
    className: 'whitespace-nowrap',
    render: (row) => formatDateTime(row.visionFirstReadAt),
  },
  {
    key: 'visionLastReadAt',
    label: 'Last read',
    group: 'Vision',
    className: 'whitespace-nowrap',
    render: (row) => formatDateTime(row.visionLastReadAt),
  },
  {
    key: 'visionBlockers',
    label: 'Blocked by',
    group: 'Vision',
    render: (row) => <BlockersCell blockers={row.visionBlockers} />,
  },
  {
    key: 'stock',
    label: 'Stock',
    group: 'Catalog',
    render: (row) => <BoolCell value={row.stock} />,
  },
  {
    key: 'isActive',
    label: 'Active',
    group: 'Catalog',
    render: (row) => <BoolCell value={row.isActive} />,
  },
  {
    key: 'seoStatus',
    label: 'Status',
    group: 'SEO',
    render: (row) => <StatusBadge status={row.seoStatus} />,
  },
  {
    key: 'seoSynced',
    label: 'Synced',
    group: 'SEO',
    render: (row) => <BoolCell value={row.seoSynced} />,
  },
  {
    key: 'seoError',
    label: 'Error',
    group: 'SEO',
    render: (row) => <ErrorCell title="SEO error" message={row.seoError} />,
  },
  {
    key: 'customerId',
    label: 'Customer ID',
    group: 'Customer',
    className: 'font-mono',
    render: (row) => text(row.customerId),
  },
  {
    key: 'customerName',
    label: 'Customer name',
    group: 'Customer',
    className: 'whitespace-nowrap',
    render: (row) => text(row.customerName),
  },
];

const GROUP_STYLES: Record<ColumnGroup, string> = {
  Product: 'bg-gray-100 text-gray-700',
  'Vision category': 'bg-purple-100 text-purple-800',
  Vision: 'bg-indigo-100 text-indigo-800',
  Catalog: 'bg-emerald-100 text-emerald-800',
  SEO: 'bg-sky-100 text-sky-800',
  Customer: 'bg-gray-100 text-gray-700',
};

export function getColumnGroups() {
  return MONITOR_COLUMNS.reduce<
    { group: ColumnGroup; span: number; className: string }[]
  >((groups, column) => {
    const last = groups[groups.length - 1];

    if (last?.group === column.group) {
      last.span += 1;
      return groups;
    }

    return [
      ...groups,
      { group: column.group, span: 1, className: GROUP_STYLES[column.group] },
    ];
  }, []);
}
